/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useMemo } from 'react'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { observer } from 'mobx-react-lite'
import { useHistory, useParams } from 'react-router-dom'

import { useMobxStore } from '../../state'
import LoadingSpinner from '../../components/LoadingSpinner'
import { Login, Main } from './steps'
import { toJS } from 'mobx'
import dayjs from 'dayjs'
import sportNinjaLogoDark from './steps/sportninja_dark.svg'
import sportNinjaLogoLight from './steps/sportninja_light.svg'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { Countdown } from 'src/components/Countdown'

const RegistrationInvite = observer(() => {
  const history = useHistory()
  const { id } = useParams()
  const {
    registrationInvite: {
      fetchEventInfo,
      state: { eventInfo, isLoading, isLogged },
    },
  } = useMobxStore()

  const isRegistrationOpen = useMemo(() => {
    const today = dayjs()
    const eventDay = dayjs(eventInfo?.registration?.starts_at)
    const isOpen = today.isAfter(eventDay) || eventInfo?.registration?.is_live

    return isOpen
  }, [eventInfo])

  const apiBackgroundImage = useMemo(() => {
    return eventInfo?.registration?.image?.full_path
  }, [eventInfo?.registration?.image])

  useEffect(() => {
    if (id) {
      fetchEventInfo(id)
    }
  }, [id])

  if (isLoading) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
        `}
      >
        <LoadingSpinner />
      </div>
    )
  }

  if (
    !eventInfo?.id ||
    !id ||
    !eventInfo.registration.online_registration_open
  ) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          flex-direction: column;
          height: 100vh;
        `}
      >
        <h1>Event not found</h1>
        <button
          css={css`
            margin-top: 20px;
            text-transform: uppercase;
            color: ${colors.DEFAULT_FLAIR};
          `}
          onClick={() => history.push(ROUTES.HOME)}
        >
          Go to home
        </button>
      </div>
    )
  }

  if (!isRegistrationOpen) {
    return (
      <div
        css={css`
          display: flex;
          height: 100vh;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          background-color: ${eventInfo?.registration?.dark_theme
            ? '#000'
            : '#fff'};
          background-image: url(${apiBackgroundImage});
          background-position: center;
          background-size: cover;
          background-repeat: no-repeat;
          flex: 2;

          > * {
            color: ${eventInfo?.registration?.dark_theme ? '#fff' : '#000'};
          }
        `}
      >
        <h1
          css={css`
            text-align: center;
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
              : 'Rift, Arial, Helvetica, sans-serif'};
            font-size: 58px;
            font-style: normal;
            font-weight: 700;
            text-transform: uppercase;
          `}
        >
          {eventInfo?.name}
        </h1>
        <p
          css={css`
            text-align: center;
            font-size: 20px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
            text-transform: uppercase;
            margin-top: 8px;
          `}
        >
          Registration will open in
        </p>

        <div
          css={css`
            margin-top: 32px;
          `}
        >
          <Countdown targetDate={eventInfo?.registration?.starts_at} isDarkMode={eventInfo?.registration?.dark_theme} />
        </div>

        <div
          css={css`
            margin-top: 32px;
            display: flex;
            align-items: center;
          `}
        >
          <p>Powered by</p>
          <img
            src={
              eventInfo?.registration?.dark_theme ? sportNinjaLogoDark : sportNinjaLogoLight
            }
            css={css`
              margin-left: 8px;
              width: 84px;
              height: 18px;
            `}
          />
        </div>
      </div>
    )
  }

  return isLogged ? <Main /> : <Login />
})

export default RegistrationInvite
